import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, X, FilePlus } from "lucide-react";
import { API } from "@/lib/api";
import { useOrgCurrency } from "@/lib/currency-context";
import { offerLabel, offerLabelPlural } from "@/lib/constants";

export const CRM_STAGES = [
  { id: "neu", label: "Neu", color: "#64748B" },
  { id: "angebot", label: "Angebot", color: "#2563EB" },
  { id: "verhandlung", label: "Verhandlung", color: "#D97706" },
  { id: "gewonnen", label: "Gewonnen", color: "#059669" },
  { id: "verloren", label: "Verloren", color: "#DC2626" },
];

export function CRMView({ company, onCreateQuoteFromDeal }) {
  const { currency, formatMoney } = useOrgCurrency();
  const singularOfferLabel = offerLabel(company);
  const pluralOfferLabel = offerLabelPlural(company);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [dragId, setDragId] = useState(null);
  const [members, setMembers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("");

  useEffect(() => {
    axios.get(`${API}/team/members`)
      .then((r) => setMembers(r.data?.members || []))
      .catch(() => setMembers([]));
  }, []);
  const memberName = (em) => {
    const m = members.find((x) => x.email === em);
    return m ? (m.name || m.email) : em;
  };

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/crm/deals`); setDeals(r.data || []); }
    catch { setDeals([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pad = (n) => String(n).padStart(2, "0");
  const d0 = new Date();
  const today = `${d0.getFullYear()}-${pad(d0.getMonth() + 1)}-${pad(d0.getDate())}`;
  const money = (v) => formatMoney(v);
  const displayedValue = (deal) => {
    const offerValue = Number(deal?.offer_value_net);
    return Number.isFinite(offerValue) ? offerValue : (Number(deal?.value) || 0);
  };
  const hasOfferValue = (deal) => Number.isFinite(Number(deal?.offer_value_net));
  const offersForDisplay = (deal) => [...(deal?.linked_offers || [])].reverse();

  const openNew = (stage) => { setNote(""); setEditing({ title: "", customer_name: "", value: 0, stage: stage || "neu", next_task_title: "", next_task_due: "", activities: [] }); };
  const openEdit = (dl) => { setNote(""); setEditing({ ...dl }); };

  const body = (dl) => ({ title: dl.title, customer_name: dl.customer_name, value: Number(dl.value) || 0, stage: dl.stage, next_task_title: dl.next_task_title, next_task_due: dl.next_task_due, probability: dl.probability === "" || dl.probability == null ? null : Math.min(100, Math.max(0, Number(dl.probability))), owner_email: dl.owner_email || "" });
  const save = async () => {
    if (!editing.title.trim()) { toast.error("Titel erforderlich"); return; }
    setSaving(true);
    try {
      if (editing.id) await axios.put(`${API}/crm/deals/${editing.id}`, body(editing));
      else await axios.post(`${API}/crm/deals`, body(editing));
      toast.success("Deal gespeichert");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!editing?.id) return;
    if (!window.confirm("Diesen Deal löschen?")) return;
    try { await axios.delete(`${API}/crm/deals/${editing.id}`); toast.success("Gelöscht"); setEditing(null); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };
  const addNote = async () => {
    if (!note.trim() || !editing?.id) return;
    try {
      const r = await axios.post(`${API}/crm/deals/${editing.id}/activity`, { text: note });
      setEditing({ ...editing, activities: [...(editing.activities || []), r.data] });
      setNote(""); load();
    } catch { toast.error("Notiz fehlgeschlagen"); }
  };
  const moveStage = async (id, stage) => {
    const dl = deals.find((x) => x.id === id);
    if (!dl || dl.stage === stage) return;
    setDeals((prev) => prev.map((x) => (x.id === id ? { ...x, stage } : x)));
    try { await axios.put(`${API}/crm/deals/${id}`, { ...body(dl), stage }); }
    catch { toast.error("Verschieben fehlgeschlagen"); load(); }
  };

  const overdue = (dl) => dl.next_task_due && dl.next_task_due < today && !["gewonnen", "verloren"].includes(dl.stage);

  return (
    <div data-testid="crm-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--brand-ink)]">Verkaufs-Pipeline</h2>
          <p className="text-sm text-[var(--brand-muted)]">Ziehe Deals zwischen den Phasen oder klicke einen Deal zum Bearbeiten.</p>
        </div>
        <div className="flex items-center gap-2">
          {members.length > 1 && (
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
              className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm" data-testid="crm-owner-filter">
              <option value="">Alle Mitarbeiter</option>
              {members.map((m) => <option key={m.id} value={m.email}>{m.name || m.email}</option>)}
            </select>
          )}
          <Button onClick={() => openNew("neu")} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="crm-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Neuer Deal
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--brand-muted)]">Lädt …</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {CRM_STAGES.map((st) => {
            const items = deals.filter((d) => d.stage === st.id && (!ownerFilter || (d.owner_email || "") === ownerFilter));
            const sum = items.reduce((a, b) => a + displayedValue(b), 0);
            return (
              <div key={st.id} data-testid={`crm-col-${st.id}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId) moveStage(dragId, st.id); setDragId(null); }}
                className="rounded-xl bg-[var(--brand-bg)] border border-[var(--brand-border-light)] p-2 min-h-[200px]">
                <div className="flex items-center justify-between px-1 py-1.5 mb-2 border-b" style={{ borderColor: st.color + "40" }}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.color }} />
                    <span className="text-sm font-semibold text-[var(--brand-ink)]">{st.id === "angebot" ? singularOfferLabel : st.label}</span>
                    <span className="text-xs text-[var(--brand-muted)]">({items.length})</span>
                  </div>
                  <span className="text-xs font-medium text-[var(--brand-muted)]">{money(sum)}</span>
                </div>
                <div className="space-y-2">
                  {items.map((dl) => (
                    <div key={dl.id} draggable
                      onDragStart={() => setDragId(dl.id)}
                      onClick={() => openEdit(dl)}
                      data-testid={`crm-card-${dl.id}`}
                      className="rounded-lg bg-white border border-[var(--brand-border-light)] p-2.5 cursor-pointer hover:border-[var(--brand-primary)] transition shadow-sm">
                      <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{dl.title}</div>
                      {dl.customer_name && <div className="text-xs text-[var(--brand-muted)] truncate mt-0.5">{dl.customer_name}</div>}
                      {dl.owner_email && <div className="text-[10px] text-[var(--brand-muted)] truncate mt-0.5" title={`Verantwortlich: ${memberName(dl.owner_email)}`}>→ {memberName(dl.owner_email)}</div>}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-semibold" style={{ color: st.color }}>{money(displayedValue(dl))}</span>
                        <span className="flex items-center gap-1">
                          {dl.probability != null && dl.probability !== "" && !["gewonnen", "verloren"].includes(dl.stage) && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--brand-bg)] border border-[var(--brand-border-light)] text-[var(--brand-muted)] font-medium" title="Abschluss-Wahrscheinlichkeit">{Math.round(dl.probability)}%</span>
                          )}
                          {dl.next_task_title && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${overdue(dl) ? "bg-red-100 text-red-700" : "bg-[var(--brand-border-light)] text-[var(--brand-muted)]"}`} title={dl.next_task_title}>
                              {dl.next_task_due ? new Date(dl.next_task_due + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "short" }) : "Aufgabe"}
                            </span>
                          )}
                        </span>
                      </div>
                      {hasOfferValue(dl) && <div className="mt-1 text-[10px] text-[var(--brand-muted)]">Neueste verknüpfte Offerte · netto</div>}
                      {(dl.linked_offers || []).length > 0 && <div className="mt-2 space-y-1 border-t border-[var(--brand-border-light)] pt-2" data-testid={`crm-card-offers-${dl.id}`}>
                        {offersForDisplay(dl).map((offer, index) => <div key={offer.id} className="flex items-center justify-between gap-2 rounded border border-[var(--brand-border-light)] bg-[var(--brand-bg)] px-2 py-1.5 text-[10px]"><div className="min-w-0"><div className="font-medium text-[var(--brand-ink)] truncate">{singularOfferLabel} {index + 1} · {offer.angebot_nr || "—"}</div><div className="text-[var(--brand-muted)]">{offer.status}</div></div><div className="shrink-0 text-right"><div className="font-medium text-[var(--brand-ink)]">{formatMoney(offer.net, offer.currency || currency)} netto</div><div className="text-[var(--brand-muted)]">Total {formatMoney(offer.gross, offer.currency || currency)}</div></div></div>)}
                        {(dl.linked_offers || []).length > 1 && <div className="pt-0.5 text-[10px] text-[var(--brand-muted)]">{pluralOfferLabel} werden als Varianten getrennt geführt und nicht addiert.</div>}
                      </div>}
                      {onCreateQuoteFromDeal && !["gewonnen", "verloren"].includes(dl.stage) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onCreateQuoteFromDeal(dl); }}
                          className="mt-2 w-full text-[11px] font-medium text-[var(--brand-primary)] border border-[var(--brand-border)] rounded-md py-1 hover:bg-[var(--brand-bg)] transition flex items-center justify-center gap-1"
                          data-testid={`crm-card-to-quote-${dl.id}`}
                          title="Angebot aus diesem Deal erstellen">
                          <FilePlus className="w-3 h-3" /> {singularOfferLabel} erstellen
                        </button>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-[var(--brand-muted)] text-center py-3">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <Card className="w-full max-w-lg bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--brand-ink)]">{editing.id ? "Deal bearbeiten" : "Neuer Deal"}</h3>
              <button onClick={() => setEditing(null)} className="p-1" data-testid="crm-modal-close"><X className="w-5 h-5 text-[var(--brand-muted)]" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Titel</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="z. B. Website-Projekt Müller GmbH" data-testid="crm-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kunde</Label>
                  <Input value={editing.customer_name} onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} data-testid="crm-customer" />
                </div>
                <div>
                  <Label>Wert ({currency})</Label>
                  <Input type="number" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="crm-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Phase</Label>
                  <select value={editing.stage} onChange={(e) => setEditing({ ...editing, stage: e.target.value })} className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="crm-stage">
                    {CRM_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Abschluss-Wahrscheinlichkeit (%)</Label>
                  <Input type="number" min="0" max="100" value={editing.probability ?? ""} onChange={(e) => setEditing({ ...editing, probability: e.target.value })}
                    placeholder={`Standard: ${{ neu: 20, angebot: 50, verhandlung: 75, gewonnen: 100, verloren: 0 }[editing.stage] ?? 20} %`} data-testid="crm-probability" />
                </div>
              </div>
              {editing.id && (editing.linked_offers || []).length > 0 && (
                <div className="rounded-lg border border-[var(--brand-border-light)] bg-[var(--brand-bg)] p-3" data-testid="crm-linked-offers">
                  <div className="text-sm font-semibold text-[var(--brand-ink)]">Verknüpfte {pluralOfferLabel}</div>
                  <p className="mt-0.5 text-xs text-[var(--brand-muted)]">Varianten bleiben getrennt und werden nicht zusammengerechnet.</p>
                  <div className="mt-2 space-y-1.5">
                    {offersForDisplay(editing).map((offer, index) => (
                      <div key={offer.id} className="flex items-center justify-between gap-2 rounded border border-[var(--brand-border-light)] bg-white px-2.5 py-2 text-xs">
                        <div className="min-w-0"><span className="font-medium text-[var(--brand-ink)]">{singularOfferLabel} {index + 1} · {offer.angebot_nr || "—"}</span><span className="text-[var(--brand-muted)]"> · {offer.status}</span></div>
                        <div className="shrink-0 text-right"><div className="font-medium text-[var(--brand-ink)]">{formatMoney(offer.net, offer.currency || currency)} netto</div><div className="text-[var(--brand-muted)]">Total {formatMoney(offer.gross, offer.currency || currency)}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {members.length > 1 && (
                <div>
                  <Label>Verantwortlich (Vertrieb)</Label>
                  <select value={editing.owner_email || ""} onChange={(e) => setEditing({ ...editing, owner_email: e.target.value })}
                    className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="crm-owner">
                    <option value="">— Nicht zugeordnet —</option>
                    {members.map((m) => <option key={m.id} value={m.email}>{m.name || m.email}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Wiedervorlage / Aufgabe</Label>
                  <Input value={editing.next_task_title} onChange={(e) => setEditing({ ...editing, next_task_title: e.target.value })} placeholder="z. B. Nachfassen anrufen" data-testid="crm-task-title" />
                </div>
                <div>
                  <Label>Fällig am</Label>
                  <Input type="date" value={editing.next_task_due} onChange={(e) => setEditing({ ...editing, next_task_due: e.target.value })} data-testid="crm-task-due" />
                </div>
              </div>

              {editing.id && (
                <div className="pt-2 border-t border-[var(--brand-border-light)]">
                  <Label>Aktivitäten & Notizen</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz hinzufügen (z. B. Telefonat)" data-testid="crm-note-input"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }} />
                    <Button variant="outline" onClick={addNote} className="border-[var(--brand-border)]" data-testid="crm-note-add">Hinzufügen</Button>
                  </div>
                  <div className="mt-3 space-y-2 max-h-40 overflow-y-auto" data-testid="crm-activities">
                    {(editing.activities || []).slice().reverse().map((a) => (
                      <div key={a.id} className="text-sm border-l-2 border-[var(--brand-primary)] pl-2.5 py-0.5">
                        <div className="text-[var(--brand-ink)]">{a.text}</div>
                        <div className="text-[11px] text-[var(--brand-muted)]">{a.at ? new Date(a.at).toLocaleString("de-DE") : ""}</div>
                      </div>
                    ))}
                    {(!editing.activities || editing.activities.length === 0) && <div className="text-xs text-[var(--brand-muted)]">Noch keine Aktivitäten.</div>}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 mt-5">
              {editing.id ? (
                <Button variant="outline" onClick={remove} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="crm-delete"><Trash2 className="w-4 h-4 mr-1" /> Löschen</Button>
              ) : <span />}
              <div className="flex items-center gap-2">
                {editing.id && onCreateQuoteFromDeal && !["gewonnen", "verloren"].includes(editing.stage) && (
                  <Button variant="outline" onClick={() => { const d = editing; setEditing(null); onCreateQuoteFromDeal(d); }} className="border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-bg)]" data-testid="crm-modal-to-quote"><FilePlus className="w-4 h-4 mr-1" /> {singularOfferLabel} erstellen</Button>
                )}
                <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="crm-save">{saving ? "Speichert …" : "Speichern"}</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
